import {
  documentRoot,
  getDataset,
  getGlobalElements,
  getWidgets,
  framePlacement,
  listFrameWidgets,
} from "../../contract/document.js";
import { resolveBreakpoint, resolvePreviewBreakpoint } from "../../contract/breakpoints.js";
import { createSpriteSheet, renderNode, renderResolvedNode } from "../../dom/paint.js";
import { renderDocument, renderPageFrame } from "../../preview/compose.js";
import { materializeWidgetNode, previewWidgetTarget } from "../../preview/widgets.js";
import { planComponentPreview } from "../../selection/preview-plan.js";
import { parseChromeKey, parseWidgetKey } from "../../selection/keys.js";

/** @param {import("../viewer-deps.js").ViewerDeps} deps */
export function createComponentStage(deps) {
  function paintComponentPagePreview(pageKey, options = {}) {
    deps.clearPreviewBreakpointHint();
    const doc = deps.getDoc();
    const started = performance.now();
    const theme = deps.getTheme();
    const viewportWidth = deps.getViewportWidth();
    const vars = deps.mergePageVars(doc, theme, pageKey);
    const onAction = deps.createPreviewOnAction(vars);
    deps.preview.replaceChildren(createSpriteSheet());
    deps.previewBlock?.classList.remove("is-overlay-sheet");
    const result = renderPageFrame(doc, {
      theme,
      viewportWidth,
      pageKey,
      onAction,
      ...deps.previewSlotOptions(),
      phaseByWidget: deps.previewPhaseByWidget(),
    });
    deps.preview.append(result.el);
    deps.setRendered(result);
    if (result.elementErrors?.length) {
      deps.errorBox.hidden = false;
      deps.errorBox.textContent = result.elementErrors.join("; ");
    } else if (options.refreshEditor !== false) {
      deps.errorBox.hidden = true;
    }
    deps.meta.innerHTML = "";
    deps.meta.append(
      deps.metaItem(`Theme: ${theme}`),
      deps.metaItem(`Page: ${pageKey}`),
      deps.metaItem(`Breakpoint: ${result.breakpoint || "base"} (${viewportWidth}px)`),
      deps.metaItem(`Components: ${result.count}`),
      deps.metaItem(`Time to render: ${(performance.now() - started).toFixed(1)}ms`),
    );
  }

  function paintComponent(options = {}) {
    deps.clearPreviewBreakpointHint();
    const doc = deps.getDoc();
    const scopeKey = deps.getPreviewScopeKey() || deps.getSelectedKey();
    const chromeFrame = parseChromeKey(scopeKey);
    if (chromeFrame?.part === "frame") {
      paintChromeFrame(chromeFrame.frameRef, options);
      return;
    }

    const pagePreviewKey = deps.resolveComponentPreviewPageKey(scopeKey, doc);
    if (pagePreviewKey) {
      if (deps.isValidComponentCard(pagePreviewKey)) {
        deps.setComponentPreviewSurface(pagePreviewKey);
      }
      paintComponentPagePreview(pagePreviewKey, options);
      return;
    }

    const started = performance.now();
    const theme = deps.getTheme();
    const viewportWidth = deps.getViewportWidth();
    const widgetName = scopeKey?.startsWith("widget:") ? parseWidgetKey(scopeKey) : null;
    const previewSurface = widgetName && deps.CHROME_SURFACES_WITH_ELEMENTS.includes(widgetName)
      ? widgetName
      : deps.resolveChromePreviewPart(deps.activeSurface());
    const { root, vars, breakpoints } = documentRoot(doc, theme, previewSurface);
    const previewElements = getGlobalElements(doc);
    const previewDatasetKind = widgetName
      ? (deps.getWidgetDatasetRecordId(widgetName, doc) ?? "")
      : undefined;
    const plan = planComponentPreview({
      doc,
      selectedKey: scopeKey,
      surfaceRoot: root,
      resolveNode: (key) => deps.resolveNodeByKey(key, doc),
      previewModeFor: (name) => deps.getWidgetPreviewMode(name, doc),
      datasetRecordIdFor: (name) => deps.getWidgetDatasetRecordId(name, doc),
      pageElements: previewElements,
      dataset: getDataset(doc),
      previewDatasetKind,
      widgets: getWidgets(doc),
      vars,
    });
    if (!plan.node) throw new Error("Нечего рисовать: нет view и выбранного element");
    const breakpoint = plan.kind === "surface"
      ? resolveBreakpoint(breakpoints, viewportWidth)
      : resolvePreviewBreakpoint(plan.node, breakpoints, viewportWidth);
    deps.preview.replaceChildren();
    const onAction = deps.createPreviewOnAction(vars);
    const slotOptions = deps.previewSlotOptions();
    const surface = deps.resolveChromePreviewPart(deps.activeSurface());
    deps.previewBlock?.classList.remove("is-overlay-sheet");
    let rendered;
    if (plan.kind === "surface") {
      rendered = renderDocument(doc, {
        onAction,
        theme,
        viewportWidth,
        surface,
        phaseByWidget: deps.previewPhaseByWidget(),
        ...slotOptions,
      });
      deps.preview.append(rendered.el);
      deps.preview.querySelector(".hv-root")?.setAttribute("data-card", surface);
    } else {
      deps.preview.replaceChildren(createSpriteSheet());
      const ctx = {
        vars,
        elements: previewElements,
        elementErrors: [],
        dataset: getDataset(doc),
        widgets: getWidgets(doc),
        previewMode: plan.previewMode,
        phaseByWidget: deps.previewPhaseByWidget(),
        onAction,
        nextId: 1,
        nodes: new Map(),
        path: plan.path,
        breakpoint,
        widgetName: widgetName ?? undefined,
        previewDatasetKind,
        groupValues: previewDatasetKind === "" ? {} : undefined,
        adminImageBaseUrl: deps.getAdminImageBaseUrl?.() || "",
        ...slotOptions,
      };
      const draw = plan.kind === "widget-element" || plan.kind === "widget-visual"
        ? renderResolvedNode
        : renderNode;
      const el = draw(plan.node, ctx);
      if (el && plan.kind === "widget-element") {
        el.style.flexShrink = "0";
        el.style.minWidth = "max-content";
      } else if (el && plan.kind === "widget-visual") {
        el.style.flexShrink = "0";
        el.style.minWidth = "0";
      }
      if (plan.kind === "widget-element") {
        const host = document.createElement("div");
        host.className = "hv-widget-element-host";
        if (el) host.append(el);
        deps.preview.append(host);
      } else if (el) {
        deps.preview.append(el);
      }
      rendered = { el, nodes: ctx.nodes, count: ctx.nodes.size, breakpoint, elementErrors: ctx.elementErrors };
    }
    deps.setRendered(rendered);
    if (rendered.elementErrors?.length) {
      deps.errorBox.hidden = false;
      deps.errorBox.textContent = rendered.elementErrors.join("; ");
    } else if (options.refreshEditor !== false) {
      deps.errorBox.hidden = true;
    }
    const ms = (performance.now() - started).toFixed(1);
    deps.meta.innerHTML = "";
    deps.meta.append(
      deps.metaItem(`Theme: ${theme}`),
      deps.metaItem(`Breakpoint: ${rendered.breakpoint || "base"} (${viewportWidth}px)`),
      deps.metaItem(`Components: ${rendered.count}`),
      deps.metaItem(`Time to render: ${ms}ms`),
    );
    deps.updatePreviewBreakpointHint(doc, plan, surface, breakpoints, viewportWidth, rendered.count);
  }

  function paintChromeFrame(frameRef, options = {}) {
    deps.clearPreviewBreakpointHint();
    const doc = deps.getDoc();
    const started = performance.now();
    const theme = deps.getTheme();
    const viewportWidth = deps.getViewportWidth();
    const { vars, breakpoints } = documentRoot(doc, theme);
    const entries = listFrameWidgets(doc).filter((entry) => !frameRef || entry.ref === frameRef);
    const breakpoint = resolveBreakpoint(breakpoints, viewportWidth);
    deps.preview.replaceChildren(createSpriteSheet());
    deps.previewBlock?.classList.remove("is-overlay-sheet");
    const host = document.createElement("div");
    host.className = "hv-node hv-container hv-root";
    host.style.display = "flex";
    host.style.flexDirection = "column";
    host.style.width = "100%";
    const onAction = deps.createPreviewOnAction(vars);
    const slotOptions = deps.previewSlotOptions();
    const ctx = {
      vars,
      elements: getGlobalElements(doc),
      elementErrors: [],
      dataset: {},
      widgets: getWidgets(doc),
      previewDatasetKind: "",
      groupValues: {},
      onAction,
      nextId: 1,
      nodes: new Map(),
      breakpoint,
      phaseByWidget: deps.previewPhaseByWidget(),
      adminImageBaseUrl: deps.getAdminImageBaseUrl?.() || "",
      ...slotOptions,
    };
    for (const entry of entries) {
      const previewMode = deps.getWidgetPreviewMode(entry.ref, doc);
      const slotData = framePlacement(doc, entry.ref)?.node?.data;
      const instance = previewWidgetTarget(entry.ref, doc, {
        previewMode,
        phaseByWidget: { [entry.ref]: previewMode },
      }) ?? { widget: entry.ref };
      const node = materializeWidgetNode(instance, {
        ...ctx,
        widgetName: entry.ref,
        previewMode,
        phaseByWidget: { [entry.ref]: previewMode },
        slotData: slotData && typeof slotData === "object" ? slotData : null,
      });
      if (!node) continue;
      const el = renderResolvedNode(node, {
        ...ctx,
        widgetName: entry.ref,
        previewMode,
        path: `chrome:frame/${entry.ref}`,
      });
      if (el) host.append(el);
    }
    deps.preview.append(host);
    const rendered = {
      el: host,
      nodes: ctx.nodes,
      count: ctx.nodes.size,
      breakpoint,
      elementErrors: ctx.elementErrors,
    };
    deps.setRendered(rendered);
    if (rendered.elementErrors?.length) {
      deps.errorBox.hidden = false;
      deps.errorBox.textContent = rendered.elementErrors.join("; ");
    } else if (options.refreshEditor !== false) {
      deps.errorBox.hidden = true;
    }
    deps.meta.innerHTML = "";
    deps.meta.append(
      deps.metaItem(`Theme: ${theme}`),
      deps.metaItem(`Breakpoint: ${rendered.breakpoint || "base"} (${viewportWidth}px)`),
      deps.metaItem(`Components: ${rendered.count}`),
      deps.metaItem(`Time to render: ${(performance.now() - started).toFixed(1)}ms`),
    );
  }

  return { paintComponent, paintComponentPagePreview };
}
